import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  TalentViewCreateData,
  TalentViewService,
  TalentViewTalentRegistrationValidateRetrieveData,
  TalentViewUploadFileCreateData,
  TalentService,
  TalentConfirmRegistrationCreateData,
  TalentConfirmRegistrationRetrieveData,
} from "api";
import i18n from 'i18next';
import { resetAppState } from "../store/common";
import { handleErrorResponse } from "../utils/util";
import { USER_ROLE } from "../constants/index";

// Register
export const register = createAsyncThunk(
  "register",
  async (data: TalentViewCreateData, thunkAPI: any) => {
    try {
      const res = await TalentViewService.talentViewCreate(data) as any;
      if (res) {
        return res;
      }
      return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.TALENT, thunkAPI);
    }
  },
);

export const uploadFileRegistration = createAsyncThunk(
  "uploadFileRegistration",
  async (data: TalentViewUploadFileCreateData, thunkAPI: any) => {
    try {
      const res = await TalentViewService.talentViewUploadFileCreate(data) as any;
      if (res) {
        return res;
      }
      return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.TALENT, thunkAPI);
    }
  },
);

export const checkTalentRegistrationToken = createAsyncThunk(
  "checkTalentRegistrationToken",
  async (data: TalentViewTalentRegistrationValidateRetrieveData, thunkAPI: any) => {
    try {
      const res = await TalentViewService.talentViewTalentRegistrationValidateRetrieve(data) as any;
      if (res.success) {
        return res;
      } else if(res.message == i18n.t('MSG_TALENT_REGISTER_CODE_DISABLE')) {
        thunkAPI.dispatch(setIsShowTopPageUrl(false));
      }
      return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.TALENT, thunkAPI);
    }
  },
);

// Get options related talent register
export const getOptionsRelatedTalentRegister = createAsyncThunk(
  "getOptionsRelatedTalentRegister",
  async (_, thunkAPI: any) => {
    try {
      const res = await TalentViewService.talentViewGetOptionsRelatedRegisterRetrieve() as any;
      if (res && res.data) {
        return res.data;
      } else {
        return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

export const getTalentConfirmRegistrationInfo = createAsyncThunk(
  "getTalentConfirmRegistrationInfo",
  async (token: string, thunkAPI: any) => {
    try {
      const data: TalentConfirmRegistrationRetrieveData = {
        authorization: `Bearer ${token}`,
        xRegistrationToken: token,
      };
      const res = await TalentService.talentConfirmRegistrationRetrieve(data) as any;
      if (res?.success && res?.data) {
        return res.data;
      }
      return thunkAPI.rejectWithValue(res?.message || i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.TALENT, thunkAPI);
    }
  },
);

export const confirmTalentRegistration = createAsyncThunk(
  "confirmTalentRegistration",
  async (payload: any, thunkAPI: any) => {
    try {
      const data: TalentConfirmRegistrationCreateData = {
        requestBody: {
          token: payload.token,
          first_name: payload.first_name,
          last_name: payload.last_name,
          password: payload.password,
          identity_verification_text: payload.identity_verification_text ?? null,
        } as any,
      };
      const res = await TalentService.talentConfirmRegistrationCreate(data) as any;
      if(res) {
        return res;
      }
      return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.TALENT, thunkAPI);
    }
  },
);

// State ban đầu của Redux
interface TalentRegisterState {
  loading: boolean;
  error: string | null;
  actionStatus: string;
  actionName: string;
  companyInfo: any;
  talentCompany: any;
  optionsRelatedCompany: any;
  isSuccess: boolean;
  resMessage: string;
  isLoadingRegister: boolean;
  talentTemp: any;
  passCheckCode: string;
  urlSlug: string;
  type: string;
  register_code: string;
  referrer_talent_id: string;
  isShowTopPageUrl: boolean;
  reasonListItems: any;
  initialData: any;
  initialErrors: any;
  talentOptionsRelatedRegister: any;
  confirmRegistrationData: any;
  confirmRegistrationLoading: boolean;
  isReloadConfirmRegistrationInfo: boolean;
  confirmRequestId: string | null;
}
const initialState: TalentRegisterState = {
  loading: false,
  error: null,
  actionStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  actionName: "",
  companyInfo: {},
  talentCompany: {},
  optionsRelatedCompany: {},
  isSuccess: true,
  resMessage: "",
  isLoadingRegister: false,
  talentTemp: {},
  passCheckCode: "checking", // 'checking' | 'passed' | 'failed'
  urlSlug: "",
  type: null,
  register_code: null,
  referrer_talent_id: null,
  isShowTopPageUrl: true,
  reasonListItems: [
      {
        name: "給与を上げたい",
        checked: false,
        value: "給与を上げたい",
      },
      {
        name: "マネジメントをしたい",
        checked: false,
        value: "マネジメントをしたい",
      },
      {
        name: "スペシャリストになりたい",
        checked: false,
        value: "スペシャリストになりたい",
      },
      {
        name: "業務内容を変えたい",
        checked: false,
        value: "業務内容を変えたい",
      },
      {
        name: "職種・業界を変えたい",
        checked: false,
        value: "職種・業界を変えたい",
      },
      {
        name: "社風・人間関係",
        checked: false,
        value: "社風・人間関係",
      },
      {
        name: "ワークライフバランスを向上させたい",
        checked: false,
        value: "ワークライフバランスを向上させたい",
      },
      {
        name: "勉強・留学等のため",
        checked: false,
        value: "勉強・留学等のため",
      },
      {
        name: "仕事を離れたい（離職）",
        checked: false,
        value: "仕事を離れたい（離職）",
      },
      {
        name: "その他",
        checked: false,
        value: "その他",
      }
    ],
    initialData: { 
      company_id: null,
      first_name: "",
      last_name: "",
      email: "",
      email2: "",
      password: "",
      password2: "",
      gender: null,
      last_name_kana: "",
      first_name_kana: "",
      yearOfBirth: null,
      monthOfBirth: null,
      dateOfBirth: null,
      referer: null,
      employee_number: null,
      city: null,
      department: null,
      profession_1: null,
      profession_2: null,
      profession_3: null,
      industry1: null,
      industry2: null,
      industry3: null,
      type: "alumni",
      yearOfJoin: null,
      monthOfJoin: null,
      yearOfLeave: null,
      monthOfLeave: null,
      dateOfLeave: null,
      reason: [],
      otherReason: null,
    },
    initialErrors: {
      company: "",
      first_name: "",
      last_name: "",
      email: "",
      email2: "",
      password: "",
      password2: "",
      gender: "",
      last_name_kana: "",
      first_name_kana: "",
      yearOfBirth: "",
      monthOfBirth: "",
      dateOfBirth: "",
      referer: "",
      employee_number: "",
      department: "",
      profession_1: "",
      profession_2: "",
      profession_3: "",
      industry1: "",
      industry2: "",
      industry3: "",
      city: "",
      yearOfJoin: "",
      monthOfJoin: "",
      yearOfLeave: "",
      reason: "",
    },
    talentOptionsRelatedRegister: {gender_options: [], jobTypeOptions: [], industryOptions: []},
    confirmRegistrationData: null,
    confirmRegistrationLoading: false,
    isReloadConfirmRegistrationInfo: false,
    confirmRequestId: null,
};
// Tạo slice với các reducers và extraReducers (cho async action)
export const talentRegisterSlice = createSlice({
  name: "talentRegister",
  initialState,
  reducers: {
    setIsLoadingRegister: (state, action) => {
      state.isLoadingRegister = action.payload;
    },
    setUrlSlug: (state, action) => {
      state.urlSlug = action.payload;
    },
    setCompanyInfo: (state, action) => {
      state.companyInfo = action.payload;
    },
    resetConfirmState: (state) => {
      state.confirmRegistrationData = null;
      state.confirmRegistrationLoading = false;
      state.confirmRequestId = null;
      state.actionName = "";
      state.actionStatus = "idle";
      state.error = null;
      state.isReloadConfirmRegistrationInfo = false;
      state.resMessage = "";
      state.isSuccess = true;
    },
    setIsShowTopPageUrl: (state, action) => {
      state.isShowTopPageUrl = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // reset state
      .addCase(resetAppState, () => initialState)

      // Talent register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.actionName = "talentRegister";
        state.actionStatus = "pending";
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.actionName = "talentRegister";
        state.actionStatus = "fulfilled";
        state.talentCompany = action.payload;
        state.isSuccess = action.payload.success;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.actionName = "talentRegister";
        state.actionStatus = "rejected";
      })

      // Talent register upload file
      .addCase(uploadFileRegistration.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.actionName = "uploadFileRegistration";
        state.actionStatus = "pending";
      })
      .addCase(uploadFileRegistration.fulfilled, (state, action) => {
        state.loading = false;
        state.actionName = "uploadFileRegistration";
        state.actionStatus = "fulfilled";
        state.isSuccess = action.payload.success;
      })
      .addCase(uploadFileRegistration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.actionName = "uploadFileRegistration";
        state.actionStatus = "rejected";
      })

      // checkTalentRegistrationToken
      .addCase(checkTalentRegistrationToken.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.actionName = "checkTalentRegistrationToken";
        state.actionStatus = "pending";
      })
      .addCase(checkTalentRegistrationToken.fulfilled, (state, action) => {
        state.loading = false;
        state.actionStatus = "fulfilled";
        state.type = action.payload.data.type;
        state.register_code = action.payload.data.register_code;
        state.referrer_talent_id = action.payload.data.referrer_talent_id;
        state.optionsRelatedCompany = action.payload.data;
      })
      .addCase(checkTalentRegistrationToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.actionStatus = "rejected";
      })

      // Get options related talent register
      .addCase(getOptionsRelatedTalentRegister.pending, (state) => {
        state.loading = true; 
        state.error = null;
      })
      .addCase(getOptionsRelatedTalentRegister.fulfilled, (state, action) => {
        state.loading = false; 
        state.talentOptionsRelatedRegister = action.payload;
      })
      .addCase(getOptionsRelatedTalentRegister.rejected, (state, action) => {
        state.loading = false; 
        state.error = action.payload as string;
      })

      .addCase(getTalentConfirmRegistrationInfo.pending, (state, action) => {
        state.confirmRegistrationLoading = true;
        state.actionName = "getTalentConfirmRegistrationInfo";
        state.actionStatus = "pending";
        state.error = null;
        state.confirmRequestId = action.meta.requestId;
      })
      .addCase(getTalentConfirmRegistrationInfo.fulfilled, (state, action) => {
        if (state.confirmRequestId !== action.meta.requestId) {
          return;
        }
        state.confirmRegistrationLoading = false;
        state.actionName = "getTalentConfirmRegistrationInfo";
        state.actionStatus = "fulfilled";
        state.type = action.payload?.talent_type?.value;
        state.companyInfo = {
          name: action.payload?.company_name,
          logo: action.payload?.company_logo,
          privacy_policy: action.payload?.company_privacy_policy || "",
        }
        state.confirmRegistrationData = action.payload;
        state.isReloadConfirmRegistrationInfo = false;
      })
      .addCase(getTalentConfirmRegistrationInfo.rejected, (state, action) => {
        if (state.confirmRequestId !== action.meta.requestId) {
          return;
        }
        state.confirmRegistrationLoading = false;
        state.actionName = "getTalentConfirmRegistrationInfo";
        state.actionStatus = "rejected";
        state.confirmRegistrationData = null;
        state.error = action.payload as string;
      })

      .addCase(confirmTalentRegistration.pending, (state) => {
        state.error = null;
        state.actionName = "confirmTalentRegistration";
        state.actionStatus = "pending";
        state.confirmRegistrationLoading = true;
      })
      .addCase(confirmTalentRegistration.fulfilled, (state, action) => {
        state.actionName = "confirmTalentRegistration";
        state.actionStatus = "fulfilled";
        state.isSuccess = action.payload.success;
        state.isReloadConfirmRegistrationInfo = action.payload.data?.is_reload_form;
        state.resMessage = action.payload.message;
        state.confirmRegistrationLoading = false;
      })
      .addCase(confirmTalentRegistration.rejected, (state, action) => {
        state.actionName = "confirmTalentRegistration";
        state.actionStatus = "rejected";
        state.confirmRegistrationLoading = false;
        state.error = action.payload as string;
      })
  },
});
export const {
  resetConfirmState,
  setIsLoadingRegister,
  setUrlSlug,
  setCompanyInfo,
  setIsShowTopPageUrl,
} = talentRegisterSlice.actions;
export default talentRegisterSlice.reducer;
